// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage the navigation and highlighting logic for Overview.
 * Abstracts tab selection, target attribute scrolling, and element highlighting.
 *
 * @param {object} params
 * @param {string|null} params.initialTabSelected - ID of the initially selected tab
 * @param {string|null} params.initialTabNameSelected - Name of the initially selected tab
 * @param {string|null} params.initialAttributeSelected - ID of the initially selected attribute to highlight
 * @param {boolean} params.isNewVariant - Whether tabbed layout variant is active
 * @param {Array} params.visibleTabs - List of visible tabs in the layout
 * @param {number} params.activeTab - Parent's current active tab index
 * @param {function} params.setActiveTab - Parent's active tab index setter
 * @param {object} params.lastHandledRedirectionRef - Parent's redirection tracking ref
 * @param {function} params.highlightElement - Parent's attribute highlighting function
 * @param {object} params.ref - Parent's forwarded ref
 */
const useOverviewRedirectionHighlight = ({
  initialTabSelected,
  initialTabNameSelected,
  initialAttributeSelected,
  isNewVariant,
  visibleTabs,
  activeTab,
  setActiveTab,
  lastHandledRedirectionRef,
  highlightElement,
  ref,
}) => {
  const redirectionRef = lastHandledRedirectionRef;

  // Pending attribute highlight for tabbed view
  const [pendingAttributeHighlight, setPendingAttributeHighlight] =
    useState(null);

  // Wraps highlightElement to prevent animations inside child elements from
  // bubbling up and ending the parent's highlight animation prematurely.
  const safeHighlight = useCallback(
    (el) => {
      if (!el) {
        return;
      }
      const stopBubbling = (event) => {
        if (event.target !== el) {
          event.stopPropagation();
        } else {
          el.removeEventListener('animationend', stopBubbling, {
            capture: true,
          });
        }
      };
      el.addEventListener('animationend', stopBubbling, { capture: true });
      highlightElement(el);
    },
    [highlightElement]
  );

  // Handle redirection logic when the tabbed layout variant is active
  const handleTabbedRedirection = useCallback(
    (tabSelected, attributeSelected) => {
      if (!tabSelected || !visibleTabs?.length) {
        if (attributeSelected) {
          setPendingAttributeHighlight(attributeSelected);
        }
        return;
      }

      const idx = visibleTabs.findIndex(
        (tab) => String(tab?.section_id) === String(tabSelected)
      );

      if (idx === -1) {
        return;
      }

      // 1. Switch active tab
      setActiveTab(idx);

      // 2. Set target attribute to be highlighted once mounted
      if (attributeSelected) {
        setPendingAttributeHighlight(attributeSelected);
      }

      // 3. Reset scroll position of right side card content to top only if not highlighting an attribute
      if (!attributeSelected) {
        setTimeout(() => {
          const cardContent = document
            .querySelector('.summary-card')
            ?.closest('.MuiBox-root');
          if (cardContent) {
            cardContent.scrollTop = 0;
          }
        }, 50);
      }

      // 4. Highlight the left sidebar tab
      setTimeout(() => {
        const tabEl = document.querySelector(
          `[data-section-tab-id="${tabSelected}"]`
        );
        if (tabEl) {
          safeHighlight(tabEl);
        }
      }, 100);
    },
    [visibleTabs, setActiveTab, safeHighlight]
  );

  // Handle redirection logic when the accordion layout variant is active
  const handleAccordionRedirection = useCallback(
    (tabSelected, tabNameSelected, attributeSelected) => {
      const refCurrent = ref && typeof ref === 'object' ? ref.current : null;
      if (!refCurrent) {
        return;
      }

      if (attributeSelected) {
        setTimeout(() => {
          if (refCurrent.scrollToAttribute) {
            refCurrent.scrollToAttribute(attributeSelected, tabNameSelected);
          }
        }, 300);
      } else if (tabSelected) {
        setTimeout(() => {
          const isPreliminaryOrOverview =
            String(tabSelected) === 'preliminary' ||
            String(tabSelected) === 'overview';

          if (isPreliminaryOrOverview) {
            if (refCurrent.scrollToPreliminary) {
              refCurrent.scrollToPreliminary();
            }
          } else if (tabNameSelected) {
            if (refCurrent.scrollToDescriptiveSection) {
              refCurrent.scrollToDescriptiveSection(tabNameSelected);
            }
          }
        }, 300);
      }
    },
    [ref]
  );

  // Hook to handle initial navigation/redirection based on props
  useEffect(() => {
    if (!initialTabSelected && !initialAttributeSelected) {
      if (redirectionRef) {
        redirectionRef.current = {
          sectionId: null,
          attributeId: null,
        };
      }
      return;
    }

    const isAlreadyHandled =
      redirectionRef?.current?.sectionId === initialTabSelected &&
      redirectionRef?.current?.attributeId === initialAttributeSelected;

    if (isAlreadyHandled) {
      return;
    }

    if (redirectionRef) {
      redirectionRef.current = {
        sectionId: initialTabSelected,
        attributeId: initialAttributeSelected,
      };
    }

    if (isNewVariant) {
      handleTabbedRedirection(initialTabSelected, initialAttributeSelected);
    } else {
      handleAccordionRedirection(
        initialTabSelected,
        initialTabNameSelected,
        initialAttributeSelected
      );
    }
  }, [
    initialTabSelected,
    initialTabNameSelected,
    initialAttributeSelected,
    isNewVariant,
    redirectionRef,
    handleTabbedRedirection,
    handleAccordionRedirection,
  ]);

  // Timing-safe attribute highlighting & scrolling after DOM mount
  useEffect(() => {
    if (!pendingAttributeHighlight) {
      return undefined;
    }
    let retryCount = 0;
    const maxRetries = 20; // try up to 2 seconds
    let timerId = null;

    const tryHighlight = () => {
      const attrEl = document.querySelector(
        `[data-attribute-id="${pendingAttributeHighlight}"]`
      );
      if (attrEl) {
        const cardContent = document
          .querySelector('.summary-card')
          ?.closest('.MuiBox-root');
        if (cardContent) {
          const containerRect = cardContent.getBoundingClientRect();
          const attrRect = attrEl.getBoundingClientRect();
          const relativeTop =
            attrRect.top - containerRect.top + cardContent.scrollTop;
          const scrollTarget =
            relativeTop - containerRect.height / 2 + attrRect.height / 2;
          cardContent.scrollTo({
            top: scrollTarget,
            behavior: 'smooth',
          });
        } else {
          attrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        attrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          safeHighlight(attrEl);
        }, 350);
        setPendingAttributeHighlight(null);
      } else if (retryCount < maxRetries) {
        retryCount++;
        timerId = setTimeout(tryHighlight, 100);
      } else {
        setPendingAttributeHighlight(null);
      }
    };

    tryHighlight();

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [activeTab, pendingAttributeHighlight, safeHighlight]);
};

export default useOverviewRedirectionHighlight;
